import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Alert,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es'; // Importar el locale español

// Configurar dayjs para usar español
dayjs.locale('es');

const DISABLE_REASONS = [
  { id: 'maintenance', label: 'Mantenimiento', requiresEndDate: true },
  { id: 'rest', label: 'Descanso', requiresEndDate: true },
  { id: 'other_operation', label: 'En otra operación', requiresEndDate: false },
  { id: 'documentation', label: 'Documentos vencidos', requiresEndDate: false },
  { id: 'unavailable', label: 'No disponible', requiresEndDate: true },
  { id: 'custom', label: 'Otro motivo', requiresEndDate: false }
];

const DisableVehicleModal = ({
  open,
  onClose,
  vehicleInfo,
  onConfirm,
  theme = 'light'
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    customReason: '',
    startDate: dayjs(),
    endDate: null,
    comment: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedReason = DISABLE_REASONS.find(r => r.id === formData.reason);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reason) {
      newErrors.reason = 'Selecciona una razón';
    }
    
    if (formData.reason === 'custom' && !formData.customReason.trim()) {
      newErrors.customReason = 'Especifica el motivo';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Fecha de inicio requerida';
    } else if (formData.startDate.isBefore(dayjs().startOf('day'))) {
      newErrors.startDate = 'La fecha no puede ser anterior a hoy';
    }
    
    if (selectedReason?.requiresEndDate && !formData.endDate) {
      newErrors.endDate = 'Fecha de fin requerida para este motivo';
    }
    
    if (formData.endDate && formData.startDate && (formData.endDate.isBefore(formData.startDate) || formData.endDate.isSame(formData.startDate))) {
      newErrors.endDate = 'La fecha de fin debe ser posterior al inicio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const reasonText = formData.reason === 'custom' 
        ? formData.customReason 
        : selectedReason.label;
        
      await onConfirm({
        reason: reasonText,
        startDate: formData.startDate.format('YYYY-MM-DD'),
        endDate: formData.endDate ? formData.endDate.format('YYYY-MM-DD') : null,
        comment: formData.comment.trim(),
        vehicleId: vehicleInfo?.vehicleId,
        relationId: vehicleInfo?.relationId
      });
      
      handleClose();
    } catch (error) {
      console.error('Error al deshabilitar vehículo:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reason: '',
      customReason: '',
      startDate: dayjs(),
      endDate: null,
      comment: ''
    });
    setErrors({});
    onClose();
  };

  const calculateDuration = () => {
    if (!formData.startDate || !formData.endDate) return '';
    const days = formData.endDate.diff(formData.startDate, 'day') + 1;
    return `${days} día${days !== 1 ? 's' : ''}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        className={`GM__${theme}-disable-vehicle-modal`}
      >
        <DialogTitle>
          <Typography variant="h6">
            Deshabilitar Vehículo
          </Typography>
          {vehicleInfo && (
            <Typography variant="subtitle2" color="textSecondary">
              {vehicleInfo.plate} - {vehicleInfo.operationName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Razón de deshabilitación */}
            <FormControl fullWidth error={!!errors.reason}>
              <InputLabel>Razón de deshabilitación</InputLabel>
              <Select
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  reason: e.target.value,
                  endDate: null // Reset end date when reason changes
                }))}
                label="Razón de deshabilitación"
              >
                {DISABLE_REASONS.map(reason => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.reason && (
                <Typography variant="caption" color="error">
                  {errors.reason}
                </Typography>
              )}
            </FormControl>

            {/* Motivo personalizado */}
            {formData.reason === 'custom' && (
              <TextField
                fullWidth
                label="Especifica el motivo"
                value={formData.customReason}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  customReason: e.target.value 
                }))}
                error={!!errors.customReason}
                helperText={errors.customReason}
                placeholder="Describe el motivo de la deshabilitación"
              />
            )}

            {/* Fechas */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="Fecha de inicio"
                value={formData.startDate}
                onChange={(date) => setFormData(prev => ({ ...prev, startDate: date }))}
                minDate={dayjs()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate
                  }
                }}
              />

              {selectedReason?.requiresEndDate && (
                <DatePicker
                  label="Fecha de fin"
                  value={formData.endDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, endDate: date }))}
                  minDate={formData.startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate
                    }
                  }}
                />
              )}
            </Box>

            {/* Duración calculada */}
            {formData.startDate && formData.endDate && (
              <Alert severity="info">
                <Typography variant="body2">
                  Duración: {calculateDuration()}
                </Typography>
              </Alert>
            )}

            {/* Comentarios adicionales */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comentarios adicionales (opcional)"
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Información adicional sobre la deshabilitación..."
            />

            {/* Información importante */}
            <Alert severity="warning">
              <Typography variant="body2">
                • El vehículo será removido de programaciones futuras en el período seleccionado<br/>
                • Las programaciones del día actual no se verán afectadas<br/>
                • Podrás reactivar el vehículo en cualquier momento
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleClose} 
            color="inherit"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            color="warning"
            disabled={isSubmitting || !formData.reason}
          >
            {isSubmitting ? 'Procesando...' : 'Deshabilitar Vehículo'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default DisableVehicleModal;